import type {
  AxisDateTimeLabelFormatsOptions,
  FormatterCallbackFunction,
  Options,
  Point,
  TooltipOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';

import { DateTime, DateTimeFormatOptions } from 'luxon';

import Highcharts from '@/components/chart/highcharts';
import { colors, roundToTwoDecimals } from '@/utils';

import { LineDrawStyle } from '../types.ts';

const markerSymbols = ['circle', 'square', 'diamond', 'triangle', 'triangle-down'];

export const textColor = colors.text.neutral[100];
const hoverTextColor = colors.text.neutral.contrast;
export const bgColor = colors.surface.cards;
export const referenceLineColor = colors.stroke.critical[100];

export const defaultChartColors = [
  colors.chart.series[1],
  colors.chart.series[2],
  colors.chart.series[3],
  colors.chart.series[4],
  colors.chart.series[5],
  colors.chart.series[6],
  colors.chart.series[7],
  colors.icon.alarm[100],
];

export const scatterPlotColor = colors.chart.series[3];
export const histogramColor = colors.chart.series[1];
export const aggregatedAreaColors = [colors.chart.series[1], colors.chart.series[2]];
export const boxPlotColor = colors.chart.series[4];

export const defaultChartDateFormat: Highcharts.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: '2-digit',
  year: 'numeric',
};
export const defaultChartDateTimeFormat: Highcharts.DateTimeFormatOptions = {
  ...defaultChartDateFormat,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

export const defaultLuxonChartDateFormat: DateTimeFormatOptions = {
  ...(defaultChartDateFormat as object),
};

export const defaultLuxonChartDateTimeFormat: DateTimeFormatOptions = {
  ...(defaultChartDateTimeFormat as object),
  hour12: false,
};

/**
 * Gets the language string from i18next instance language.
 * Falls back to 'en' if not available.
 */
export const getLocaleFromLanguage = (language: string | undefined | null): string => {
  return language || 'en';
};

/**
 * Updates the chart's locale if it differs from the current locale.
 * Updates without redrawing to allow caller to control when redraw happens.
 * @param chart - The Highcharts chart instance (can be undefined/null)
 * @param locale - The desired locale string (e.g., 'en', 'no')
 */
export const updateChartLocale = (
  chart: Highcharts.Chart | undefined | null,
  locale: string
): void => {
  if (!chart || chart.options.lang?.locale === locale) {
    return;
  }

  chart.update(
    {
      lang: {
        locale: locale,
      },
    },
    false
  ); // false = don't redraw yet, caller will handle redraw
};

/**
 * Standardized datetime label formats for Highcharts axes.
 * These formats adapt based on the time range.
 * Format: { millisecond, second, minute, hour, day, week, month, year }
 * Since Highcharts 13 each unit takes `{ main, boundary? }`; `boundary` is pinned
 * to the `main` format to keep the pre-13 look (no special boundary-tick styling).
 */
export const dateTimeLabelFormats: AxisDateTimeLabelFormatsOptions = {
  millisecond: { main: '%H:%M:%S.%L' },
  second: { main: '%H:%M:%S' },
  minute: { main: '%H:%M' },
  hour: { main: '%H:%M' },
  day: { main: '%e. %b', boundary: '%e. %b' },
  week: { main: '%e. %b' },
  month: { main: "%b '%y" },
  year: { main: '%Y' },
};

const labelFontSize = '14px';
const axisLabelFontSize = '12px';
const chartFontSize = '18px';

export const scatterPlotMarkerRadius = 0.75;
export const markerEnabledThreshold = 5;
export const markerRadius = 3;
export const lineChartMarkerRadius = 1.5;
export const lineWidth = 1.75;
const defaultHoverLineWidthPlus = 1;

/**
 * Line/marker options for a 'line' series. With the 'points' draw style the line is hidden and the markers
 * are always shown, so the mode can later be switched with `series.update(...)`.
 */
export const getLineSeriesStyle = (drawStyle?: LineDrawStyle) => ({
  lineWidth: drawStyle === LineDrawStyle.POINTS ? 0 : lineWidth,
  states: {
    hover: { lineWidthPlus: drawStyle === LineDrawStyle.POINTS ? 0 : defaultHoverLineWidthPlus },
  },
  marker: {
    enabled: drawStyle === LineDrawStyle.POINTS ? true : undefined,
    enabledThreshold: drawStyle === LineDrawStyle.POINTS ? 0 : markerEnabledThreshold,
    radius: lineChartMarkerRadius,
    lineWidth: 0,
  },
});

/**
 * Creates default x-axis options with localized datetime formatting.
 * Note: dateTimeLabelFormats should be set per chart instance using dateTimeLabelFormats
 * based on the current locale from i18next.
 */
export const defaultXAxisOptions: XAxisOptions = {
  type: 'datetime',
  lineColor: textColor,
  tickColor: textColor,
  labels: {
    style: {
      color: textColor,
      fontSize: axisLabelFontSize,
    },
  },
  title: {
    style: {
      color: textColor,
    },
  },
};

export const defaultYAxisOptions: YAxisOptions = {
  lineColor: textColor,
  tickColor: textColor,
  gridLineWidth: 0.5,
  gridLineColor: textColor,
  minorGridLineWidth: 0.25,
  labels: {
    style: {
      color: textColor,
      fontSize: axisLabelFontSize,
    },
  },
  title: {
    style: {
      color: textColor,
    },
  },
};

export const getDefaultTooltipPointFormatter = (unit?: string): FormatterCallbackFunction<Point> =>
  function () {
    return `${this.series.name}: <strong>${this.y !== undefined ? roundToTwoDecimals(this.y) : ''}</strong> ${unit ?? ''}<br>`;
  };

export const defaultTooltipOptions: TooltipOptions = {
  headerFormat: '{point.key}<br>', // This should be replaced with '{point.x}<br>' when the X-axis is not a datetime axis
  pointFormatter: getDefaultTooltipPointFormatter(),
  xDateFormat: defaultChartDateTimeFormat,
  useHTML: true,
};

export const defaultChartOptions: Options = {
  time: {
    timezone: DateTime.local().zoneName,
  },
  title: {
    text: '',
  },
  colors: defaultChartColors,
  chart: {
    backgroundColor: bgColor,
    style: {
      color: textColor,
      fontSize: chartFontSize,
    },
    // Use parent container height so chart can follow grid widget resizing.
    height: null,
  },
  legend: {
    itemStyle: {
      color: textColor,
      fontSize: labelFontSize,
    },
    itemHoverStyle: {
      color: hoverTextColor,
    },
  },
  xAxis: defaultXAxisOptions,
  yAxis: defaultYAxisOptions,
  tooltip: defaultTooltipOptions,
  boost: {
    useGPUTranslations: true,
  },
  credits: { enabled: false },
};

/**
 * Creates Highcharts options with the current locale from the i18n instance.
 * This ensures charts are initialized with the correct locale and can be updated
 * when the language changes.
 *
 * @param baseOptions - The base Highcharts options to extend
 * @param language - The current language string, fetched from the i18next instance
 * @returns Highcharts options with locale set
 */
export const getChartOptionsWithLocale = (
  baseOptions: Options,
  language: string | undefined | null
): Options => {
  return {
    ...baseOptions,
    lang: {
      ...baseOptions.lang,
      locale: getLocaleFromLanguage(language),
    },
  };
};

/**
 * Returns a marker symbol for a series by index, cycling through circle, square, diamond, triangle, and triangle-down.
 * The symbol is cycled only once all colors have been used, so that series of the same color will have different markers.
 *
 * @param seriesIndex - Index of the series
 * @returns The Highcharts marker symbol name for that series
 */
export const getMarkerSymbol = (seriesIndex: number) => {
  let symbolIndex = Math.floor(seriesIndex / defaultChartColors.length);
  while (symbolIndex >= markerSymbols.length) symbolIndex -= markerSymbols.length;
  return markerSymbols[symbolIndex];
};
